/**
 * Stores the notification action that launched the app (cold start) so Shell
 * can handle navigation once the user is authenticated. Listeners in Shell are
 * set up only after auth, so they can miss the event when the app was fully closed.
 */
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import type { ActionPerformed as LocalNotificationActionPerformed } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import type { ActionPerformed as PushNotificationActionPerformed } from '@capacitor/push-notifications';
import Platform from 'utils/Platform';

export type PendingLaunchNotification =
  | { source: 'local'; action: LocalNotificationActionPerformed }
  | { source: 'push'; action: PushNotificationActionPerformed };

let pending: PendingLaunchNotification | null = null;

export function setPendingLaunchNotification(payload: PendingLaunchNotification): void {
  pending = payload;
}

export function getAndClearPendingLaunchNotification(): PendingLaunchNotification | null {
  const value = pending;
  pending = null;
  return value;
}

/**
 * Register the listener as early as possible (when this module is first imported).
 * On cold start the native side may fire the event before React mounts; storing
 * it here allows Shell to handle it once the user is authenticated.
 */
if (Platform.isNative() && Capacitor.isPluginAvailable('LocalNotifications')) {
  LocalNotifications.addListener('localNotificationActionPerformed', notificationAction => {
    setPendingLaunchNotification({ source: 'local', action: notificationAction });
  });
}

if (Platform.isNative() && Capacitor.isPluginAvailable('PushNotifications')) {
  PushNotifications.addListener('pushNotificationActionPerformed', notificationAction => {
    setPendingLaunchNotification({ source: 'push', action: notificationAction });
  });
}
